import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Packet } from '../art/Packet'
import type { ClassSpec, Diagram, FlowSpec, SeqSpec } from './parse'

// CosmicDiagram renders a parsed diagram with the same cosmic engine as the plan graph:
// SVG bezier edges, packets flowing along them (animateMotion), hover-focus dimming. One
// look across flowcharts, sequence diagrams, and UML class diagrams — no Mermaid library.
export function CosmicDiagram({ diagram }: { diagram: Diagram }) {
  if (diagram.kind === 'flow') return <FlowDiagram spec={diagram} />
  if (diagram.kind === 'seq') return <SequenceDiagram spec={diagram} />
  return <ClassDiagram spec={diagram} />
}

// Stage scales a fixed-size diagram down to fit the doc column (never up), so a wide
// flowchart isn't clipped. Unlike a plain fit-to-width, it never shrinks below MIN_SCALE —
// keeping the labels readable — and lets a still-too-wide diagram scroll horizontally instead.
const MIN_SCALE = 0.85
function Stage({ width, height, kind, children }: { width: number; height: number; kind: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const [cw, setCw] = useState(0)
  // Which edges have content beyond the frame. The font floor above guarantees this
  // happens — measured on the OAuth2 architecture flowchart, 5% of it sits past the
  // right edge at 1440, 33% at 1024 and 70% at phone width. It always scrolled; nothing
  // ever SAID so, and an overlay scrollbar on macOS is invisible until you already know.
  // A diagram that silently ends mid-node reads as a broken render, not as a scroller.
  const [edges, setEdges] = useState<'none' | 'right' | 'left' | 'both'>('none')
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const read = () => {
      const slack = el.scrollWidth - el.clientWidth
      if (slack <= 1) return setEdges('none')
      const l = el.scrollLeft > 1
      const r = el.scrollLeft < slack - 1
      setEdges(l && r ? 'both' : l ? 'left' : 'right')
    }
    const ro = new ResizeObserver((e) => {
      setCw(e[0]?.contentRect.width ?? 0)
      read()
    })
    ro.observe(el)
    el.addEventListener('scroll', read, { passive: true })
    read()
    return () => {
      ro.disconnect()
      el.removeEventListener('scroll', read)
    }
  }, [])
  const scale = cw > 0 ? Math.max(MIN_SCALE, Math.min(1, cw / width)) : 1
  const scrolls = edges !== 'none'
  // The scaled box carries the real layout footprint so horizontal scroll is exact (a CSS
  // transform alone leaves the natural width behind and over-scrolls).
  return (
    <div
      ref={ref}
      className="cdiag-fit"
      data-edges={edges}
      // Only a scrollable region becomes a tab stop, and only then does it need a name.
      // Without this a keyboard user cannot reach the hidden third of the diagram at all
      // — the scroll container took no focus, so the arrow keys had nothing to act on.
      {...(scrolls ? { tabIndex: 0, role: 'region', 'aria-label': `${kind} diagram, scrolls horizontally` } : {})}
    >
      <div className="cdiag-scaled" style={{ width: Math.round(width * scale), height: Math.round(height * scale) }}>
        <div className={`cdiag ${kind}`} style={{ width, height, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          {children}
        </div>
      </div>
    </div>
  )
}

const PACKET = (d: string, i: number, cls = '') => (
  <Packet key={'pk' + i} d={d} r={3} dur={2 + (i % 4) * 0.45} className={`packet${cls}`} />
)

// ---- flowchart ------------------------------------------------------------------------

const NW = 156
const NH = 52
const GX = 76
const GY = 26
const PAD = 18

// layerize assigns each node a column = longest path from a root (a simple Sugiyama layer
// assignment). Cycles are bounded by a depth cap so a bad LLM graph can't hang the render.
function layerize(ids: string[], edges: Array<{ from: string; to: string }>): Map<string, number> {
  const succ = new Map<string, string[]>()
  const indeg = new Map<string, number>()
  ids.forEach((id) => {
    succ.set(id, [])
    indeg.set(id, 0)
  })
  for (const e of edges) {
    if (!succ.has(e.from) || !succ.has(e.to) || e.from === e.to) continue
    succ.get(e.from)!.push(e.to)
    indeg.set(e.to, (indeg.get(e.to) ?? 0) + 1)
  }
  const layer = new Map<string, number>()
  const cap = ids.length
  const visit = (id: string, depth: number) => {
    if (depth > cap) return
    if ((layer.get(id) ?? -1) >= depth) return
    layer.set(id, depth)
    for (const n of succ.get(id) ?? []) visit(n, depth + 1)
  }
  const roots = ids.filter((id) => (indeg.get(id) ?? 0) === 0)
  ;(roots.length ? roots : ids).forEach((r) => visit(r, 0))
  ids.forEach((id) => {
    if (!layer.has(id)) layer.set(id, 0)
  })
  return layer
}

function FlowDiagram({ spec }: { spec: FlowSpec }) {
  const vertical = spec.dir === 'TD' || spec.dir === 'TB'
  const { pos, width, height } = useMemo(() => {
    const ids = spec.nodes.map((n) => n.id)
    const layer = layerize(ids, spec.edges)
    const byLayer = new Map<number, string[]>()
    for (const id of ids) {
      const l = layer.get(id) ?? 0
      const arr = byLayer.get(l) ?? []
      arr.push(id)
      byLayer.set(l, arr)
    }
    const p = new Map<string, { x: number; y: number }>()
    let maxLayer = 0
    let maxRow = 1
    for (const [l, arr] of byLayer) {
      maxLayer = Math.max(maxLayer, l)
      maxRow = Math.max(maxRow, arr.length)
      arr.forEach((id, i) => {
        const along = PAD + l * (vertical ? NH + GY * 2 : NW + GX)
        const cross = PAD + i * (vertical ? NW + GX : NH + GY)
        p.set(id, vertical ? { x: cross, y: along } : { x: along, y: cross })
      })
    }
    return {
      pos: p,
      width: PAD * 2 + (vertical ? maxRow * (NW + GX) - GX : (maxLayer + 1) * (NW + GX) - GX),
      height: PAD * 2 + (vertical ? (maxLayer + 1) * (NH + GY * 2) - GY * 2 : maxRow * (NH + GY) - GY),
    }
  }, [spec, vertical])

  const [hover, setHover] = useState<string | null>(null)
  const adj = useMemo(() => {
    const m = new Map<string, Set<string>>()
    const ensure = (id: string) => {
      let s = m.get(id)
      if (!s) {
        s = new Set([id])
        m.set(id, s)
      }
      return s
    }
    spec.nodes.forEach((n) => ensure(n.id))
    for (const e of spec.edges) {
      ensure(e.from).add(e.to)
      ensure(e.to).add(e.from)
    }
    return m
  }, [spec])
  const focus = hover ? adj.get(hover) ?? null : null

  const edges = spec.edges.flatMap((e, i) => {
    const a = pos.get(e.from)
    const b = pos.get(e.to)
    if (!a || !b) return []
    const [x1, y1, x2, y2] = vertical
      ? [a.x + NW / 2, a.y + NH, b.x + NW / 2, b.y]
      : [a.x + NW, a.y + NH / 2, b.x, b.y + NH / 2]
    const d = vertical
      ? `M ${x1} ${y1} C ${x1} ${y1 + 30}, ${x2} ${y2 - 30}, ${x2} ${y2}`
      : `M ${x1} ${y1} C ${x1 + 36} ${y1}, ${x2 - 36} ${y2}, ${x2} ${y2}`
    const on = !focus || e.from === hover || e.to === hover
    return [{ ...e, d, i, on, mx: (x1 + x2) / 2, my: (y1 + y2) / 2 }]
  })

  return (
    <Stage width={width} height={height} kind="flow">
      <svg className="cdiag-edges" width={width} height={height}>
        {edges.map((e) => (
          <path key={'e' + e.i} d={e.d} className={`edge${e.dashed ? ' dashed' : ''}${e.on ? '' : ' faded'}`} markerEnd="url(#cd-arrow)" />
        ))}
        {edges.map((e) => PACKET(e.d, e.i, e.on ? '' : ' faded'))}
        {edges.map((e) =>
          e.label ? (
            <text key={'l' + e.i} x={e.mx} y={e.my - 5} className={`cdiag-elabel${e.on ? '' : ' faded'}`} textAnchor="middle">
              {e.label}
            </text>
          ) : null,
        )}
        <defs>
          <marker id="cd-arrow" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto">
            <path d="M0,0 L7,3 L0,6 Z" className="cdiag-arrowhead" />
          </marker>
        </defs>
      </svg>
      {spec.nodes.map((n) => {
        const p = pos.get(n.id)
        if (!p) return null
        const on = !focus || focus.has(n.id)
        return (
          <div
            key={n.id}
            className={`cdiag-node shape-${n.shape}${on ? '' : ' faded'}`}
            style={{ left: p.x, top: p.y, width: NW, minHeight: NH }}
            onMouseEnter={() => setHover(n.id)}
            onMouseLeave={() => setHover(null)}
          >
            {n.label}
          </div>
        )
      })}
    </Stage>
  )
}

// ---- sequence -------------------------------------------------------------------------

const LANE = 150
const TOP = 56
const MSG_GAP = 52
const SPAD = 16

function SequenceDiagram({ spec }: { spec: SeqSpec }) {
  const laneX = (a: string) => SPAD + spec.actors.indexOf(a) * LANE + LANE / 2
  const width = SPAD * 2 + spec.actors.length * LANE
  const height = TOP + spec.messages.length * MSG_GAP + 40
  const lifelineBottom = height - 20

  return (
    <Stage width={width} height={height} kind="seq">
      <svg className="cdiag-edges" width={width} height={height}>
        <defs>
          <marker id="cd-arrow-s" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto">
            <path d="M0,0 L7,3 L0,6 Z" className="cdiag-arrowhead" />
          </marker>
        </defs>
        {spec.actors.map((a) => (
          <line key={'ll' + a} x1={laneX(a)} y1={TOP} x2={laneX(a)} y2={lifelineBottom} className="cdiag-lifeline" />
        ))}
        {spec.messages.map((m, i) => {
          const y = TOP + 28 + i * MSG_GAP
          const x1 = laneX(m.from)
          const x2 = laneX(m.to)
          const self = m.from === m.to
          const d = self
            ? `M ${x1} ${y} C ${x1 + 46} ${y - 6}, ${x1 + 46} ${y + 18}, ${x1} ${y + 18}`
            : `M ${x1} ${y} L ${x2} ${y}`
          return (
            <g key={'m' + i}>
              <path d={d} className={`edge${m.dashed ? ' dashed' : ''}`} markerEnd="url(#cd-arrow-s)" />
              {PACKET(d, i)}
              <text x={self ? x1 + 50 : (x1 + x2) / 2} y={y - 8} className="cdiag-elabel" textAnchor={self ? 'start' : 'middle'}>
                {m.text}
              </text>
            </g>
          )
        })}
      </svg>
      {spec.actors.map((a) => (
        <div key={'h' + a} className="cdiag-actor" style={{ left: SPAD + spec.actors.indexOf(a) * LANE, width: LANE }}>
          {a}
        </div>
      ))}
    </Stage>
  )
}

// ---- class ----------------------------------------------------------------------------

const CW = 188
const CHEAD = 34
const CROW = 20
const CPADV = 10

function ClassDiagram({ spec }: { spec: ClassSpec }) {
  const boxH = (memberCount: number) => CHEAD + Math.max(0, memberCount) * CROW + (memberCount ? CPADV : 8)
  const { pos, width, height } = useMemo(() => {
    const ids = spec.classes.map((c) => c.name)
    const layer = layerize(
      ids,
      spec.relations.map((r) => ({ from: r.from, to: r.to })),
    )
    const byLayer = new Map<number, string[]>()
    ids.forEach((id) => {
      const l = layer.get(id) ?? 0
      const arr = byLayer.get(l) ?? []
      arr.push(id)
      byLayer.set(l, arr)
    })
    const hByName = new Map(spec.classes.map((c) => [c.name, boxH(c.members.length)]))
    const p = new Map<string, { x: number; y: number }>()
    let maxLayer = 0
    let maxColH = 0
    for (const [l, arr] of byLayer) {
      maxLayer = Math.max(maxLayer, l)
      let y = PAD
      for (const id of arr) {
        p.set(id, { x: PAD + l * (CW + GX + 30), y })
        y += (hByName.get(id) ?? 60) + 28
      }
      maxColH = Math.max(maxColH, y)
    }
    return { pos: p, width: PAD * 2 + (maxLayer + 1) * (CW + GX + 30) - (GX + 30), height: maxColH + PAD }
  }, [spec])

  const edges = spec.relations.flatMap((r, i) => {
    const a = pos.get(r.from)
    const b = pos.get(r.to)
    if (!a || !b) return []
    const x1 = a.x + CW
    const y1 = a.y + CHEAD / 2
    const x2 = b.x
    const y2 = b.y + CHEAD / 2
    const d = `M ${x1} ${y1} C ${x1 + 34} ${y1}, ${x2 - 34} ${y2}, ${x2} ${y2}`
    return [{ ...r, d, i, mx: (x1 + x2) / 2, my: (y1 + y2) / 2 }]
  })

  return (
    <Stage width={width} height={height} kind="uml">
      <svg className="cdiag-edges" width={width} height={height}>
        <defs>
          <marker id="cd-tri" markerWidth="13" markerHeight="11" refX="11" refY="5" orient="auto">
            <path d="M0,0 L11,5 L0,10 Z" className="cdiag-tri" />
          </marker>
          <marker id="cd-arrow-c" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto">
            <path d="M0,0 L7,3 L0,6 Z" className="cdiag-arrowhead" />
          </marker>
        </defs>
        {edges.map((e) => (
          <path
            key={'e' + e.i}
            d={e.d}
            className={`edge${e.kind === 'dep' ? ' dashed' : ''}`}
            markerEnd={e.kind === 'inherit' ? 'url(#cd-tri)' : 'url(#cd-arrow-c)'}
          />
        ))}
        {edges.map((e) => PACKET(e.d, e.i))}
        {edges.map((e) =>
          e.label ? (
            <text key={'l' + e.i} x={e.mx} y={e.my - 5} className="cdiag-elabel" textAnchor="middle">
              {e.label}
            </text>
          ) : null,
        )}
      </svg>
      {spec.classes.map((c) => {
        const p = pos.get(c.name)
        if (!p) return null
        return (
          <div key={c.name} className="cdiag-class" style={{ left: p.x, top: p.y, width: CW }}>
            <div className="cdiag-class-name">{c.name}</div>
            {c.members.length ? (
              <div className="cdiag-class-members">
                {c.members.map((m, i) => (
                  <div key={i} className="cdiag-member">
                    {m}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )
      })}
    </Stage>
  )
}
